import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Sparkles, MessageSquare, Zap, Code2, ArrowRight } from 'lucide-react';

export const Route = createFileRoute('/products/aicodeforge')({
  component: AICodeForgePage,
});

const features = [
  { icon: Brain, title: 'AI-Powered Analysis', description: 'Leverage large language models to understand complex code patterns and architectural decisions.' },
  { icon: MessageSquare, title: 'Natural Language Queries', description: 'Ask questions about your codebase in plain English and get accurate, contextual answers.' },
  { icon: Code2, title: 'Code Generation', description: 'Generate boilerplate, tests, and documentation based on existing code patterns.' },
  { icon: Zap, title: 'Instant Insights', description: 'Get real-time suggestions for code improvements, bug fixes, and optimizations.' },
];

function AICodeForgePage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="h-10 w-10 text-purple-500" />
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">AI-CodeForge</h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              The next generation of AI-powered code intelligence. Understand, query, and generate code with unprecedented accuracy.
            </p>
          </div>
          <section className="mb-24">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card key={feature.title}>
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-purple-500" />
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
          <section className="mb-24">
            <Card className="bg-purple-500/5 border-purple-500/20 max-w-3xl mx-auto">
              <CardContent className="py-12">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold mb-4">What Makes AI-CodeForge Different?</h2>
                  <p className="text-muted-foreground">
                    Unlike generic AI coding assistants, AI-CodeForge is built on top of ForgeNetwork's deep code understanding capabilities.
                  </p>
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="font-semibold mb-2">Context-Aware</h3>
                    <p className="text-sm text-muted-foreground">Understands your entire codebase, not just individual files.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="font-semibold mb-2">Architecture-Informed</h3>
                    <p className="text-sm text-muted-foreground">Respects your existing patterns and architectural decisions.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="font-semibold mb-2">Security-First</h3>
                    <p className="text-sm text-muted-foreground">Generated code follows security best practices by default.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="font-semibold mb-2">Team-Ready</h3>
                    <p className="text-sm text-muted-foreground">Learns from your team's coding style and conventions.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
          <section>
            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20 max-w-2xl mx-auto">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                  Connect your repository and let AI-CodeForge transform how you write and maintain code.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button className="bg-purple-600 hover:bg-purple-700 gap-2">
                      Get Started Free <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="outline">View Pricing</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
